import { UseFormSetValue, UseFormWatch } from "react-hook-form";
import { FieldDisplayType } from "../types";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { useCurrencies } from "@/entities/settings/hooks";
import { getCurrencySymbol } from "@/pages/deals/helpers";
import OptionsManager from "../components/OptionsManager";
import LookupOptionsPanel from "../components/LookupOptionsPanel";
import CheckboxAppearanceSelector from "../components/CheckboxAppearanceSelector";
import ChecklistAppearancePreview from "../components/ChecklistAppearancePreview";
import { NONE_OPTION_VALUE } from "@/components/controls/Form/Form";
import { useTranslation } from "react-i18next";
import { map } from "lodash";
import { OptionItem } from "../utils/fieldFormUtils";
import {
  FILE_FIELD_ANY_TYPE,
  FILE_FIELD_CATEGORY_EXTENSIONS,
  FILE_FIELD_CATEGORY_KEYS
} from "@/utils/entityFieldsUtils/fileFieldUtils";

const TRANSLATION_KEY = "settings.deals.fieldConfiguration";

interface FieldConfigurationProps {
  fieldType: FieldDisplayType;
  formData: any;
  setValue: UseFormSetValue<Record<string, any>>;
  watch: UseFormWatch<Record<string, any>>;
  onOptionsManagerChange?: (data: {
    options: string[];
    allowUsersToAdd: boolean;
    defaultValue: string;
    checkboxInlineText?: string;
  }) => void;
  onHiddenToggle?: (id: number | string) => void;
  isPresetFieldEdit?: boolean;
  isEditableSystemField?: boolean;
  // Field key when this is a lookup-backed field (renders the registry panel).
  lookupFieldKey?: string;
}

export function useFieldConfiguration() {
  const { t } = useTranslation();
  const currencies = useCurrencies().data ?? [];

  const renderFieldConfiguration = ({
    fieldType,
    formData,
    onOptionsManagerChange,
    onHiddenToggle,
    isPresetFieldEdit = false,
    isEditableSystemField = false,
    lookupFieldKey
  }: FieldConfigurationProps): FormSectionProps | null => {
    if (!fieldType) return null;

    switch (fieldType) {
      case FieldDisplayType.CURRENCY:
        return {
          id: "currency-field-configuration",
          title: t(`${TRANSLATION_KEY}.title`),
          fields: [
            {
              name: "defaultValue",
              type: "singleSelect",
              label: t(`${TRANSLATION_KEY}.currency`),
              required: true,
              defaultValue: formData?.defaultValue || "",
              fieldActionProps: {
                fieldName: "defaultValue",
                selectedValue: formData?.defaultValue || "",
                options: map(currencies, (currencyCode) => ({
                  id: currencyCode,
                  displayName: `${getCurrencySymbol(currencyCode)} ${currencyCode}`
                })),
                placeholder: t(`${TRANSLATION_KEY}.selectCurrency`),
                formatter: (value: string) => {
                  return `${getCurrencySymbol(value)} ${value}`;
                },
                onSelection: (value: string | number) => {
                  if (onOptionsManagerChange) {
                    onOptionsManagerChange({
                      options: [],
                      allowUsersToAdd: false,
                      defaultValue: value as string
                    });
                  }
                }
              }
            }
          ]
        };

      case FieldDisplayType.SWITCH:
        const option1Value =
          formData?.option1 || t(`${TRANSLATION_KEY}.option1Default`);
        const option2Value =
          formData?.option2 || t(`${TRANSLATION_KEY}.option2Default`);

        return {
          id: "switch-field-configuration",
          title: t(`${TRANSLATION_KEY}.title`),
          fieldsContainerClassName: "gap-0",
          fields: [
            {
              name: "option1",
              type: "text",
              label: t(`${TRANSLATION_KEY}.toggleBetween`),
              required: false,
              defaultValue: formData?.option1 || "",
              fieldActionProps: {
                placeholder: t(`${TRANSLATION_KEY}.option1Placeholder`),
                value: formData?.option1 || ""
              }
            },
            {
              name: "option2",
              type: "text",
              label: "",
              required: false,
              defaultValue: formData?.option2 || "",
              fieldActionProps: {
                placeholder: t(`${TRANSLATION_KEY}.option2Placeholder`),
                value: formData?.option2 || ""
              }
            },
            {
              name: "defaultValue",
              type: "singleSelect",
              label: t(`${TRANSLATION_KEY}.defaultEntry`),
              required: false,
              fieldClassName: "mt-2",
              defaultValue: formData?.defaultValue || "option1",
              fieldActionProps: {
                fieldName: "defaultValue",
                selectedValue: formData?.defaultValue || "option1",
                options: [
                  {
                    id: NONE_OPTION_VALUE,
                    displayName: t(`${TRANSLATION_KEY}.none`)
                  },
                  { id: "option1", displayName: option1Value },
                  { id: "option2", displayName: option2Value }
                ],
                placeholder: t(`${TRANSLATION_KEY}.selectDefaultOption`)
              }
            }
          ]
        };

      case FieldDisplayType.CHECKBOX:
        return {
          id: "checkbox-field-configuration",
          title: t(`${TRANSLATION_KEY}.title`),
          fields: [
            {
              name: "defaultValue",
              type: "singleSelect" as const,
              label: t(`${TRANSLATION_KEY}.defaultEntry`),
              required: false,
              defaultValue: formData?.defaultValue || "unchecked",
              fieldActionProps: {
                fieldName: "defaultValue",
                selectedValue: formData?.defaultValue || "unchecked",
                options: [
                  {
                    id: "checked",
                    displayName: t(`${TRANSLATION_KEY}.checked`)
                  },
                  {
                    id: "unchecked",
                    displayName: t(`${TRANSLATION_KEY}.unchecked`)
                  }
                ],
                placeholder: t(`${TRANSLATION_KEY}.selectDefaultState`),
                onSelection: (value: string | number) => {
                  if (onOptionsManagerChange) {
                    onOptionsManagerChange({
                      options: [],
                      allowUsersToAdd: false,
                      defaultValue: value as string,
                      checkboxInlineText: formData?.checkboxInlineText
                    });
                  }
                }
              }
            }
          ],
          bottomCustomComponent: (
            <CheckboxAppearanceSelector
              initialInlineText={formData?.checkboxInlineText}
              onAppearanceChange={(inlineText) => {
                if (onOptionsManagerChange) {
                  onOptionsManagerChange({
                    options: [],
                    allowUsersToAdd: false,
                    defaultValue: formData?.defaultValue || "unchecked",
                    checkboxInlineText: inlineText
                  });
                }
              }}
            />
          )
        };

      case FieldDisplayType.SINGLE_SELECT:
        return {
          id: "single-select-field-configuration",
          title: t(`${TRANSLATION_KEY}.title`),
          fields: [],
          bottomCustomComponent: lookupFieldKey ? (
            <LookupOptionsPanel
              fieldKey={lookupFieldKey}
              onOptionsChange={onOptionsManagerChange || (() => {})}
              onHiddenToggle={onHiddenToggle}
              initialDefaultValue={formData?.defaultValue || ""}
            />
          ) : (
            <OptionsManager
              initialOptions={formData?.options || [""]}
              initialOptionItems={formData?.optionItems as OptionItem[] | undefined}
              initialAllowUsersToAdd={formData?.allowUsersToAdd || false}
              initialDefaultValue={formData?.defaultValue || ""}
              fieldType="single"
              onOptionsChange={onOptionsManagerChange || (() => {})}
              onHiddenToggle={onHiddenToggle}
              isPresetFieldEdit={isPresetFieldEdit}
              isEditableSystemField={isEditableSystemField}
            />
          )
        };

      case FieldDisplayType.MULTI_SELECT:
        return {
          id: "multi-select-field-configuration",
          title: t(`${TRANSLATION_KEY}.title`),
          fields: [],
          bottomCustomComponent: (
            <OptionsManager
              initialOptions={formData?.options || [""]}
              initialOptionItems={formData?.optionItems as OptionItem[] | undefined}
              initialAllowUsersToAdd={formData?.allowUsersToAdd || false}
              initialDefaultValue={formData?.defaultValue || ""}
              fieldType="multi"
              onOptionsChange={onOptionsManagerChange || (() => {})}
              onHiddenToggle={onHiddenToggle}
              isPresetFieldEdit={isPresetFieldEdit}
              isEditableSystemField={isEditableSystemField}
            />
          )
        };

      case FieldDisplayType.NUMBER:
        return {
          id: "number-field-configuration",
          title: t(`${TRANSLATION_KEY}.limitValueRange`),
          sectionClassName: "gap-6",
          fields: [
            {
              name: "minValue",
              type: "number",
              label: t(`${TRANSLATION_KEY}.minimumAllowed`),
              required: false,
              layout: "half",
              defaultValue: formData?.minValue ?? null,
              fieldActionProps: {
                placeholder: t(`${TRANSLATION_KEY}.enterMinimumValue`),
                defaultValue: formData?.minValue ?? ""
              }
            },
            {
              name: "maxValue",
              type: "number",
              label: t(`${TRANSLATION_KEY}.maximumAllowed`),
              required: false,
              layout: "half",
              defaultValue: formData?.maxValue ?? null,
              fieldActionProps: {
                placeholder: t(`${TRANSLATION_KEY}.enterMaximumValue`),
                defaultValue: formData?.maxValue ?? ""
              }
            }
          ]
        };

      case FieldDisplayType.CHECKLIST_ITEM:
        return {
          id: "checklist-item-field-configuration",
          sectionClassName: "gap-0",
          fields: [],
          bottomCustomComponent: <ChecklistAppearancePreview />
        };

      case FieldDisplayType.FILE_UPLOAD:
        return {
          id: "file-upload-field-configuration",
          title: t(`${TRANSLATION_KEY}.title`),
          fields: [
            {
              name: "supportedFiles",
              type: "singleSelect",
              label: t(`${TRANSLATION_KEY}.supportedFiles`),
              subLabel: t(`${TRANSLATION_KEY}.supportedFilesSubLabel`),
              required: true,
              defaultValue: formData?.supportedFiles || FILE_FIELD_ANY_TYPE,
              hintText: t(`${TRANSLATION_KEY}.fileSizeLimit`),
              fieldActionProps: {
                fieldName: "supportedFiles",
                selectedValue: formData?.supportedFiles || FILE_FIELD_ANY_TYPE,
                options: FILE_FIELD_CATEGORY_KEYS.map((categoryKey) => ({
                  id: categoryKey,
                  displayName: t(
                    `${TRANSLATION_KEY}.fileCategories.${categoryKey}`
                  ),
                  subtitle: FILE_FIELD_CATEGORY_EXTENSIONS[categoryKey]
                    ?.split(",")
                    .join(", ")
                })),
                placeholder: t(`${TRANSLATION_KEY}.selectSupportedFiles`)
              }
            },
            {
              name: "allowMultipleFiles",
              type: "checkbox",
              label: "",
              required: false,
              defaultValue: formData?.allowMultipleFiles ?? true,
              fieldActionProps: {
                placeholder: "",
                label: t(`${TRANSLATION_KEY}.allowMultipleFiles`)
              }
            }
          ]
        };

      // Simple field types that don't need configuration
      case FieldDisplayType.TEXT:
      case FieldDisplayType.TEXTAREA:
      case FieldDisplayType.DATE:
      case FieldDisplayType.DATE_RANGE:
      case FieldDisplayType.EMAIL:
      case FieldDisplayType.URL:
      case FieldDisplayType.PERCENTAGE:
        return null;

      default:
        return null;
    }
  };

  return { renderFieldConfiguration };
}
